import { SHADOW_TOKENS } from "./shadowTokens";
/** Canonical defaults. Independent of MUI, CSS and browser state. */
export const DEFAULT_THEME_TOKENS = Object.freeze({
  primary_base: "#007368",
  primary_base_hover: "#005A51",
  primary_tint: "#D8F4EF",
  primary_tint_hover: "#B4E8DF",
  primary_brand: "#04A999",
  surface_dark: "#102D39",
  surface_dark_hover: "#0D4A4F",
  status_error: "#BB001C",
  status_error_hover: "#930013",
  status_error_tint: "#FFE6E4",
  status_warning: "#7B5F00",
  status_warning_tint: "#FFEBBC",
  status_success: "#007651",
  status_success_tint: "#D1F8E4",
  status_info: "#0065B0",
  status_info_tint: "#E0EFFF",
  neutral_white: "#FFFFFF",
  neutral_700: "#404040",
  neutral_600: "#606060",
  neutral_500: "#808080",
  neutral_300: "#C1C1C1",
  neutral_200: "#E0E0E0",
  neutral_100: "#EFEFEF",
  neutral_50: "#F9F9F9",
  color_menu_text: "#FFFFFF",
  ...SHADOW_TOKENS,
  font_family_Montserrat: '"Montserrat", sans-serif',
  font_family_Avenir: '"AvenirLTStd-Roman"',
  font_family_Avenir_Black: '"AvenirLTStd-Black"',
  font_size: "14px",
  font_size_small: "12px",
  font_size_medium: "16px",
  font_size_large: "20px",
  font_size_xlarge: "24px",
  font_size_xxlarge: "28px",
  font_size_xxxlarge: "32px",
  font_weight_light: "300",
  font_weight_regular: "400",
  font_weight_medium: "500",
  font_weight_semibold: "600",
  font_weight_bold: "700",
  font_weight_extrabold: "800",
  spacing_unit: "8px",
  radius_control: "4px",
  radius_container: "8px",
  radius_round: "9999px",
  radius_focus_ring: "6px",
  font_family: '"Open Sans", sans-serif',
});

export type ThemeTokenKey = keyof typeof DEFAULT_THEME_TOKENS;
export type AppThemeTokens = Record<ThemeTokenKey, string>;
export const cssTokenName = (key: string) => `--${key.replace(/_/g, "-")}`;

export const isStyleToken = (key: string) =>
  /^(font_|shadow_|spacing_|radius_)/.test(key);

/** Component overrides may select a role, never introduce another radius. */
export function resolveRadiusReference(
  value: unknown,
  fallback: string
): string {
  return typeof value === "string" &&
    /^var\(--radius-(control|container|round|focus-ring)\)$/.test(value)
    ? value
    : fallback;
}

/** Canvas APIs require concrete numbers rather than browser CSS references. */
export function resolveRadiusNumber(
  role: Extract<ThemeTokenKey, `radius_${string}`>,
  tokens: Partial<AppThemeTokens> = DEFAULT_THEME_TOKENS
): number {
  const value = Number.parseFloat(tokens[role] ?? DEFAULT_THEME_TOKENS[role]);
  return Number.isFinite(value)
    ? value
    : Number.parseFloat(DEFAULT_THEME_TOKENS[role]);
}
